import { supabase } from '../lib/supabase';

export interface PlatformPublicStats {
  totalUsers: number;
  resumesOptimized: number;
  applicationsTracked: number;
  interviewsLogged: number;
  avgAtsImprovementPct: number;
}

export interface ApprovedTestimonial {
  id: string;
  quote: string;
  userDisplayName: string;
  roleLanded: string;
  companyName: string;
  rating: number;
}

export interface SuccessStory {
  id: string;
  name: string;
  role: string;
  location?: string;
  kind: 'interviews' | 'ats' | 'quote';
  beforeLabel: string;
  afterLabel: string;
  beforeValue: string;
  afterValue: string;
  quote?: string;
  rating: number;
  source: 'verified' | 'representative';
}

/** Credible ratings only — never perfect 5.0 (reads as fake). */
const FALLBACK_RATINGS = [4.5, 4, 3.5, 4.5, 4, 3.5, 4, 4.5];

export function resolveStoryRating(id: string, stored?: number | null): number {
  let rating: number;
  if (stored != null && Number.isFinite(stored) && stored >= 3 && stored <= 5) {
    rating = Math.round(stored * 2) / 2;
  } else {
    const hash = id.split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
    rating = FALLBACK_RATINGS[hash % FALLBACK_RATINGS.length];
  }
  return Math.min(4.5, rating);
}

type RpcStatsRow = {
  total_users?: number;
  resumes_optimized?: number;
  applications_tracked?: number;
  interviews_logged?: number;
  avg_ats_improvement_pct?: number;
};

/** Interview-call outcomes — we help users get calls, not guaranteed jobs. */
export const REPRESENTATIVE_SUCCESS_STORIES: SuccessStory[] = [
  {
    id: 'rep-rahul',
    name: 'Rahul K.',
    role: 'Software Engineer',
    location: 'Bengaluru',
    kind: 'interviews',
    beforeLabel: 'Recruiter calls / mo',
    afterLabel: 'After GoGrabJob',
    beforeValue: '2',
    afterValue: '7',
    quote: 'I stopped spraying applications and focused on roles where my match score was high. Interview calls went from 2 to 7 a month.',
    rating: 4.5,
    source: 'representative',
  },
  {
    id: 'rep-priya',
    name: 'Priya M.',
    role: 'Product Manager',
    location: 'Mumbai',
    kind: 'interviews',
    beforeLabel: 'HR phone screens',
    afterLabel: 'In 10 days',
    beforeValue: '0',
    afterValue: '3',
    quote: 'Fixed ATS gaps the optimizer flagged — three HR calls followed. I still had to interview, but at least the phone started ringing.',
    rating: 4,
    source: 'representative',
  },
  {
    id: 'rep-ananya',
    name: 'Ananya S.',
    role: 'Data Analyst',
    location: 'Hyderabad',
    kind: 'interviews',
    beforeLabel: 'After 40+ applications',
    afterLabel: 'In 2 weeks',
    beforeValue: '0 calls',
    afterValue: '5 callbacks',
    quote: 'I only needed interview calls, not promises. Five recruiters called back after I fixed what the ATS score showed.',
    rating: 4.5,
    source: 'representative',
  },
  {
    id: 'rep-vikram',
    name: 'Vikram T.',
    role: 'Backend Developer',
    location: 'Pune',
    kind: 'interviews',
    beforeLabel: 'Screening calls',
    afterLabel: 'In 3 weeks',
    beforeValue: '0',
    afterValue: '4',
    quote: 'Four screening calls in three weeks after I aligned my resume to each JD. No job guarantee — just real conversations.',
    rating: 4,
    source: 'representative',
  },
  {
    id: 'rep-meera',
    name: 'Meera R.',
    role: 'UX Designer',
    location: 'Chennai',
    kind: 'interviews',
    beforeLabel: 'Recruiter calls',
    afterLabel: 'After ATS fix',
    beforeValue: 'Silent',
    afterValue: '3 calls',
    quote: 'Missing keywords were killing me. Three recruiter calls in ten days once I addressed them — that was the win.',
    rating: 3.5,
    source: 'representative',
  },
  {
    id: 'rep-arjun',
    name: 'Arjun P.',
    role: 'DevOps Engineer',
    location: 'Gurugram',
    kind: 'interviews',
    beforeLabel: 'Calls / month',
    afterLabel: 'Now',
    beforeValue: '1',
    afterValue: '5',
    quote: 'One call a month was normal. Tailoring faster with GoGrabJob pushed me to five — still interviewing, but the pipeline moved.',
    rating: 4.5,
    source: 'representative',
  },
  {
    id: 'rep-kavya',
    name: 'Kavya N.',
    role: 'Business Analyst',
    location: 'Kolkata',
    kind: 'interviews',
    beforeLabel: 'After 50 applications',
    afterLabel: 'Same week',
    beforeValue: '0 invites',
    afterValue: '2 calls',
    quote: 'Score went from 52 to 84. Two interview invites the same week — calls, not just automated rejections.',
    rating: 4,
    source: 'representative',
  },
  {
    id: 'rep-sanjay',
    name: 'Sanjay D.',
    role: 'QA Engineer',
    location: 'Jaipur',
    kind: 'interviews',
    beforeLabel: 'Phone screens',
    afterLabel: 'In 7 days',
    beforeValue: '0',
    afterValue: '2 booked',
    quote: 'I do not expect anyone to hand me a job. Two phone screens in a week was enough proof the resume changes worked.',
    rating: 3.5,
    source: 'representative',
  },
  {
    id: 'rep-neha',
    name: 'Neha G.',
    role: 'Marketing Manager',
    location: 'Delhi NCR',
    kind: 'interviews',
    beforeLabel: 'Recruiter outreach',
    afterLabel: 'In 2 weeks',
    beforeValue: 'None',
    afterValue: '3 screens',
    quote: 'Three screening calls in two weeks after matching each resume to the JD. Still job hunting — finally in real conversations.',
    rating: 4,
    source: 'representative',
  },
];

export function formatProofNumber(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '0';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M+`;
  if (n >= 10_000) return `${Math.floor(n / 1_000)}k+`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}k+`;
  return `${n.toLocaleString()}+`;
}

function parseRpcJsonObject<T extends Record<string, unknown>>(data: unknown): T {
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    return data as T;
  }
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data) as unknown;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as T;
      }
    } catch {
      return {} as T;
    }
  }
  return {} as T;
}

function parseRpcJsonArray<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data) as unknown;
      return Array.isArray(parsed) ? (parsed as T[]) : [];
    } catch {
      return [];
    }
  }
  return [];
}

type RpcTestimonialRow = {
  id?: string;
  quote?: string;
  user_display_name?: string;
  role_landed?: string;
  company_name?: string;
  rating?: number | string | null;
};

export async function fetchPlatformPublicStats(): Promise<PlatformPublicStats | null> {
  try {
    const { data, error } = await supabase.rpc('get_public_platform_stats');
    if (error) {
      console.warn('[platformSocialProof] stats RPC:', error.message);
      return null;
    }
    const row = parseRpcJsonObject<RpcStatsRow>(data);
    return {
      totalUsers: Number(row.total_users ?? 0),
      resumesOptimized: Number(row.resumes_optimized ?? 0),
      applicationsTracked: Number(row.applications_tracked ?? 0),
      interviewsLogged: Number(row.interviews_logged ?? 0),
      avgAtsImprovementPct: Number(row.avg_ats_improvement_pct ?? 0),
    };
  } catch (err) {
    console.warn('[platformSocialProof] stats fetch failed:', err);
    return null;
  }
}

function mapTestimonialRows(rows: RpcTestimonialRow[]): ApprovedTestimonial[] {
  return rows.map((row) => {
    const id = String(row.id ?? '');
    const rawRating = row.rating != null ? Number(row.rating) : null;
    return {
      id,
      quote: String(row.quote ?? ''),
      userDisplayName: String(row.user_display_name ?? ''),
      roleLanded: String(row.role_landed ?? ''),
      companyName: String(row.company_name ?? ''),
      rating: resolveStoryRating(id, rawRating),
    };
  });
}

export async function fetchApprovedTestimonials(limit = 12): Promise<ApprovedTestimonial[]> {
  try {
    const { data, error } = await supabase.rpc('get_public_approved_testimonials', {
      p_limit: limit,
    });

    if (!error) {
      const rows = parseRpcJsonArray<RpcTestimonialRow>(data);
      if (rows.length > 0) return mapTestimonialRows(rows);
    } else {
      console.warn('[platformSocialProof] testimonials RPC:', error.message);
    }

    const { data: tableData, error: tableError } = await supabase
      .from('testimonials')
      .select('id, quote, user_display_name, role_landed, company_name, rating')
      .eq('approved', true)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (tableError) {
      console.warn('[platformSocialProof] testimonials table:', tableError.message);
      return [];
    }

    return mapTestimonialRows((tableData ?? []) as RpcTestimonialRow[]);
  } catch (err) {
    console.warn('[platformSocialProof] testimonials fetch failed:', err);
    return [];
  }
}

const VERIFIED_INTERVIEW_FRAMES = [
  { beforeLabel: 'Recruiter calls', afterLabel: 'After optimizing', beforeValue: 'Few', afterValue: 'More calls' },
  { beforeLabel: 'Phone screens', afterLabel: 'Within weeks', beforeValue: '0–1', afterValue: 'Multiple' },
  { beforeLabel: 'After applying', afterLabel: 'Outcome', beforeValue: 'Silence', afterValue: 'Callbacks' },
];

export function testimonialsToStories(testimonials: ApprovedTestimonial[]): SuccessStory[] {
  return testimonials.map((t, index) => {
    const frame = VERIFIED_INTERVIEW_FRAMES[index % VERIFIED_INTERVIEW_FRAMES.length];
    return {
      id: t.id,
      name: t.userDisplayName,
      role: t.roleLanded,
      location: t.companyName,
      kind: 'interviews',
      beforeLabel: frame.beforeLabel,
      afterLabel: frame.afterLabel,
      beforeValue: frame.beforeValue,
      afterValue: frame.afterValue,
      quote: t.quote,
      rating: t.rating,
      source: 'verified',
    };
  });
}

export function pickSuccessStories(testimonials: ApprovedTestimonial[]): SuccessStory[] {
  const verified = testimonialsToStories(testimonials);
  const merged = [...verified];
  for (const story of REPRESENTATIVE_SUCCESS_STORIES) {
    if (merged.length >= 12) break;
    const dup = merged.some(
      (s) => s.name.toLowerCase() === story.name.toLowerCase(),
    );
    if (!dup) merged.push(story);
  }
  return merged.length > 0 ? merged : REPRESENTATIVE_SUCCESS_STORIES;
}
